// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import type { TyLatLng } from "@/types";
import Icon from "@/UI/atoms/Icon";
import NumberInput from "@/UI/inputs/NumberInput";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = { value: TyLatLng | null; onChange: (value: TyLatLng | null) => void; label?: string; disabled?: boolean };

/**
 * The map, before there is a map.
 *
 * A real one means choosing a provider — a Google key to buy and bill, or Leaflet and OpenStreetMap
 * tiles — and that decision has not been made. What is already decided is the **data**: the point is
 * stored as GeoJSON with a `2dsphere` index, and the API speaks `{lat, lng}` at its edges.
 *
 * So this holds the right value in the right shape and draws a square where the map will go. Replacing
 * it later is one component, and nothing that was entered through it is lost.
 */
export default function MapInput({ value, onChange, label, disabled }: Props) {
    const t = useTranslation();

    /**
     * A point is only a point when both halves are numbers. Anything else — a half-filled form, or a
     * raw GeoJSON object that slipped through an endpoint that forgot to convert it — counts as "no
     * location" rather than being rendered, which is what used to take the whole form down.
     */
    const point = Number.isFinite(value?.lat) && Number.isFinite(value?.lng) ? (value as TyLatLng) : null;

    const set = (part: Partial<TyLatLng>) => {
        const next = { lat: point?.lat ?? 0, lng: point?.lng ?? 0, ...part };
        // Both halves or neither — half a coordinate is a point in the sea.
        if (!Number.isFinite(next.lat) || !Number.isFinite(next.lng)) return onChange(null);
        onChange(next);
    };

    return (
        <div className="map-input">
            {label && <span className="map-input__label">{label}</span>}

            <div className="map-input__canvas" aria-hidden="true">
                <Icon name="properties" size={26} />
                <span className="map-input__note">{t.properties.mapPlaceholder}</span>
                {point && (
                    <span className="map-input__point">
                        {point.lat.toFixed(5)}, {point.lng.toFixed(5)}
                    </span>
                )}
            </div>

            <div className="map-input__coords">
                <NumberInput value={point?.lat ?? null} onChange={(lat) => set({ lat: lat ?? NaN })} label="Lat" min={-90} disabled={disabled} />
                <NumberInput value={point?.lng ?? null} onChange={(lng) => set({ lng: lng ?? NaN })} label="Lng" min={-180} disabled={disabled} />
            </div>
        </div>
    );
}
